import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import Seo from "@/components/feature/Seo";
import JsonLd from "@/components/feature/JsonLd";
import SectionHeading from "@/components/base/SectionHeading";
import { clinicInfo } from "@/mocks/site";
import ContactForm from "./components/ContactForm";

interface ContactCard {
  icon: string;
  title: string;
  value: string;
  href: string;
  action: string;
}

const contactCards: ContactCard[] = [
  {
    icon: "ri-phone-fill",
    title: "Call the clinic",
    value: clinicInfo.phoneDisplay,
    href: clinicInfo.phoneHref,
    action: "Call Now",
  },
  ...(clinicInfo.email
    ? [
        {
          icon: "ri-mail-line",
          title: "Email us",
          value: clinicInfo.email,
          href: `mailto:${clinicInfo.email}`,
          action: "Send Email",
        },
      ]
    : []),
  {
    icon: "ri-whatsapp-line",
    title: "Chat on WhatsApp",
    value: "Send us a message and our team will reply during clinic hours.",
    href: clinicInfo.whatsappHref,
    action: "Chat with us on WhatsApp",
  },
  {
    icon: "ri-map-pin-line",
    title: "Visit us",
    value: clinicInfo.addressFull,
    href: clinicInfo.mapDirections,
    action: "Get Directions",
  },
  ...(clinicInfo.hours.length
    ? [
        {
          icon: "ri-time-line",
          title: "Opening hours",
          value: clinicInfo.hours.join(" · "),
          href: "/appointment",
          action: "Book Appointment",
        },
      ]
    : []),
];

export default function Contact() {
  return (
    <Layout>
      <Seo
        title="Contact Ortho Spine Care | Orthopaedic & Spine Clinic in Trichy"
        description="Contact Ortho Spine Care in Srirangam, Trichy. Call +91 99433 16050, message us on WhatsApp, or send us a message to book a consultation."
        path="/contact"
      />
      <JsonLd
        id="ld-contact"
        data={{
          "@context": "https://schema.org",
          "@type": "MedicalClinic",
          name: clinicInfo.name,
          telephone: clinicInfo.phoneDisplay,
          ...(clinicInfo.email ? { email: clinicInfo.email } : {}),
          url: "https://orthospine.care/contact",
          address: {
            "@type": "PostalAddress",
            streetAddress: clinicInfo.addressLine1,
            addressLocality: clinicInfo.city,
            addressRegion: clinicInfo.region,
            postalCode: "620005",
            addressCountry: "IN",
          },
        }}
      />

      <PageHero
        title="Contact Us"
        subtitle="We are here to answer your questions and help you arrange a consultation with our orthopaedic and spine specialist."
        crumbs={[{ label: "Home", to: "/" }, { label: "Contact" }]}
      />

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {contactCards.map((card) => (
              <div key={card.title} className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                  <i className={`${card.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">{card.title}</h2>
                <p className="mt-2 flex-1 font-body text-sm leading-relaxed text-foreground-600">{card.value}</p>
                <a
                  href={card.href}
                  target={card.href.startsWith("http") ? "_blank" : undefined}
                  rel={card.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  aria-label={card.title === "Chat on WhatsApp" ? "Chat with us on WhatsApp" : undefined}
                  className="mt-4 inline-flex items-center gap-1.5 font-label text-sm font-semibold text-primary-700 hover:text-primary-800"
                >
                  {card.action}
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-14 md:py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 md:px-6 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow="Send a message"
              title="Get in touch with our team"
              description="Share your question and our team will respond as soon as possible. For appointment requests, you can also use the booking form."
              align="left"
            />
            <div className="mt-7">
              <ContactForm />
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <iframe
                title="Map showing Ortho Spine Care location in Srirangam, Trichy"
                src={clinicInfo.mapEmbed}
                width="100%"
                height="320"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
              <div className="p-6">
                <h3 className="font-heading text-base font-semibold text-foreground-950">Clinic address</h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-foreground-600">{clinicInfo.addressFull}</p>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <a
                    href={clinicInfo.mapDirections}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-navigation-line" aria-hidden="true"></i>
                    Get Directions
                  </a>
                  <a
                    href={clinicInfo.whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Chat with us on WhatsApp"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-primary-300 px-5 py-2.5 font-label text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
                  >
                    <i className="ri-whatsapp-line" aria-hidden="true"></i>
                    WhatsApp
                  </a>
                  <a
                    href={clinicInfo.phoneHref}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-primary-300 px-5 py-2.5 font-label text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
                  >
                    <i className="ri-phone-fill" aria-hidden="true"></i>
                    Call Now
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-lg border border-background-200 bg-secondary-50 p-6">
              <h3 className="font-heading text-base font-semibold text-foreground-950">Appointment requests</h3>
              <p className="mt-2 font-body text-sm leading-relaxed text-secondary-900">
                Looking to book a visit? Use our appointment form for a faster response, or call the clinic directly.
              </p>
              <Link
                to="/appointment"
                className="mt-4 inline-flex items-center gap-1.5 font-label text-sm font-semibold text-primary-700 hover:text-primary-800"
              >
                Book an Appointment
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}